import { Component, EventEmitter, Injector, Input, Output, ViewEncapsulation } from '@angular/core';
import { ExtendedSearchService } from '../../../../utilities/fi-services/extended-search.service';
import { ObjectTypes } from '../../../../shared/types/object-type';
import { ObjectTypesService } from '../../../../utilities/fi-services/object-types.service';
import { OperationOverlayLargeNg4Service } from '../../../../utilities/operation-overlay-large/operation-overlay-large.service';
import { RoleService } from '../../../../role/services/role.service';
import { SessionService } from '../../../../utilities/fi-services/session.service';
import { ExtendedRoleRightService } from '../../../../role/services/extended-role-right.service';
import { BusinessUnitService } from '../../../../business-unit/business-unit.service';
import { BusinessUnitResolverService } from '../../../../business-unit/business-unit-resolver.service';
import { AccessRightService } from '../../../../shared/fi-shared-services/access-right.service';
import { DataFieldService } from '../../../../data-field/data-field.service';
import { IADGroupMappingListModel } from '../group-mapping-definitions';
import { AppTextService } from '../../../../shared/app-text';
import { FiDialogsService } from '../../../../shared/fi-dialogs';

interface IMappingType {
    ObjectType: number;
}

@Component({
    selector: 'fi-ad-external-platform-integration-group-mapping-select-type-cell',
    templateUrl: './select-type-cell.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./select-type-cell.component.scss'],
    standalone: false,
})
export class FiADIntegrationGroupMappingSelectTypeCellComponent {
    @Input() data: IADGroupMappingListModel;
    @Input() disabled: boolean = false;

    @Output() typeChange = new EventEmitter();

    constructor(
        private injector: Injector,
        private operationOverlayLarge: OperationOverlayLargeNg4Service,
        private roleService: RoleService,
        private sessionService: SessionService,
        private extendedRoleRightService: ExtendedRoleRightService,
        private businessUnitService: BusinessUnitService,
        private businessUnitResolver: BusinessUnitResolverService,
        private accessRightService: AccessRightService,
        private dataFieldService: DataFieldService,
        private fiDialogsService: FiDialogsService,
        private extendedSearch: ExtendedSearchService,
        private objectTypesService: ObjectTypesService,
        private appTextService: AppTextService,
    ) {}

    types: IMappingType[] = [
        {
            ObjectType: ObjectTypes.Role,
        },
        {
            ObjectType: ObjectTypes.BusinessUnit,
        },
    ];

    getObjectTypeName(ObjectType) {
        return this.objectTypesService.GetNameByObjectType(ObjectType);
    }

    setData(objectType, objectId, objectName) {
        this.data.ObjectType = objectType;
        this.data.ObjectId = objectId;
        this.data.ObjectName = objectName;
        this.typeChange.emit();
    }

    selectObjectTypeClick(objectType) {
        const alreadyMappedParent = this._checkSelectObjectTypeClick();
        if (alreadyMappedParent) {
            this.fiDialogsService.error({
                messageText: this.appTextService.get('ad_integration.error_mapped_by_another_container', {
                    '{group_name}': alreadyMappedParent.GroupName,
                }),
            });
        } else if (isAnythingMapped(this.data.ChildGroups)) {
            this.fiDialogsService
                .confirm({
                    messageText: this.appTextService.get('ad_integration.warning_override_mapping'),
                    headerText: this.appTextService.get('common.warning'),
                })
                .onYesButtonClick(() => this._openObjectTypeSelect(objectType));
        } else {
            this._openObjectTypeSelect(objectType);
        }
    }

    _openObjectTypeSelect(objectType) {
        this.extendedSearch
            .openExtendedSearch(objectType, {
                multiple: false,
                afterListLoad: (listData) => {
                    if (this.data.ObjectId) {
                        const selectedItem = listData.Data.find((item) => item.Id === this.data.ObjectId);
                        if (selectedItem) {
                            selectedItem.Checked = true;
                            listData.SelectedItems.push(selectedItem);
                        }
                    }
                },
            })
            .then((result) => {
                this.setData(objectType, result.Id, result.Name);
            });
    }

    private _checkSelectObjectTypeClick() {
        if (this.data.ObjectType) {
            return false;
        }

        const containersIds = getContainerIdsFromTree(this.data);

        return checkChildGroups(this.data.ChildGroups);

        // it is not allowed to change group mapping if one of it's children already mapped by another group
        function checkChildGroups(ChildGroups: IADGroupMappingListModel[]) {
            let i;
            for (i = 0; i < ChildGroups.length; i++) {
                const child = ChildGroups[i],
                    parentMapped = child.ParentGroups.find((item) => {
                        return !!item.ObjectType && containersIds.indexOf(item.GroupId) === -1;
                    });
                if (parentMapped) {
                    return parentMapped;
                }

                const parentMappedInChildren = checkChildGroups(child.ChildGroups);
                if (parentMappedInChildren) {
                    // return parentMappedInChildren;
                    return getTopmostMappedContainer(parentMappedInChildren);
                }
            }
        }
    }

    clearMappingClick() {
        this.setData(null, null, null);
    }

    getSelectObjectButtonState(objectType) {
        if (!this.data.ObjectId) {
            return 'empty';
        } else if (this.data.ObjectType == objectType) {
            return 'change';
        } else {
            return 'change_to';
        }
    }
}

function getContainerIdsFromTree(rootItem: IADGroupMappingListModel) {
    return rootItem.ChildGroups.reduce(
        (prev, child) => {
            if (child.IsContainer) {
                return prev.concat(getContainerIdsFromTree(child));
            } else {
                return prev;
            }
        },
        [rootItem.GroupId],
    );
}

function getTopmostMappedContainer(child: IADGroupMappingListModel): IADGroupMappingListModel {
    const parent = child.ParentGroups.find((item) => !!item.ObjectType);
    if (!parent) {
        return child;
    }

    return getTopmostMappedContainer(parent) || child;
}

function isAnythingMapped(data: IADGroupMappingListModel[]) {
    return data.some((item) => !!item.ObjectId || isAnythingMapped(item.ChildGroups));
}
